import { apiClient } from "../index";

const API_URL = import.meta.env.VITE_API_BASE_URL || "/api";
const REGISTER_PATH = "/api/v1/auth/register";
const LOGIN_PATH = "/api/v1/auth/login";
const UNIVERSITY_LOGIN_PATH = "/api/v1/auth/university/login";
const COMPANY_LOGIN_PATH = "/api/v1/auth/company/login";
const PLATFORM_LOGIN_PATH = "/api/v1/auth/platform/login";
const REFRESH_PATH = "/api/v1/auth/refresh";
const FORGOT_PASSWORD_PATH = "/api/v1/auth/forgot-password";
const RESEND_VERIFICATION_PATH = "/api/v1/auth/resend-verification";
const LOGOUT_PATH = "/api/v1/auth/logout";
const ME_PATH = "/api/v1/auth/me";

// Helper to decode JWT and check expiration
export function decodeToken(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch (e) {
    return null;
  }
}

export function isTokenExpired(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  // Token is expired if it has already passed
  return decoded.exp * 1000 < Date.now();
}

export function isTokenExpiringSoon(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  // Check if token expires in next 60 seconds
  return decoded.exp * 1000 < Date.now() + 60000;
}

export function getTokenFromCookie(name) {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
}

export function setTokenCookie(name, value, days = 7) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();

  // Only use Secure flag when accessed via HTTPS
  const isSecure = window.location.protocol === "https:";
  const secureFlag = isSecure ? "Secure" : "";

  // Use Lax for better compatibility (allows cookies on top-level navigations)
  document.cookie =
    `${name}=${value}; ${expires}; path=/; ${secureFlag}; SameSite=Lax`.trim();
}

export function clearTokenCookies() {
  document.cookie =
    "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie =
    "refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

export async function registerUser(payload) {
  return apiClient.post(REGISTER_PATH, payload);
}

export async function loginUser(username, password) {
  const payload = {
    username,
    password,
  };
  const response = await apiClient.post(LOGIN_PATH, payload);

  // Store tokens in secure cookies
  if (response.access_token) {
    setTokenCookie("access_token", response.access_token, 7);
    setTokenCookie("refresh_token", response.refresh_token, 7);
  }

  return response;
}

export async function loginUniversityUser(username, password) {
  const payload = {
    username,
    password,
  };
  const response = await apiClient.post(UNIVERSITY_LOGIN_PATH, payload);

  // Store tokens in secure cookies
  if (response.access_token) {
    setTokenCookie("access_token", response.access_token, 7);
    setTokenCookie("refresh_token", response.refresh_token, 7);
  }

  return response;
}

export async function loginCompanyUser(username, password) {
  const payload = {
    username,
    password,
  };
  const response = await apiClient.post(COMPANY_LOGIN_PATH, payload);

  // Store tokens in secure cookies
  if (response.access_token) {
    setTokenCookie("access_token", response.access_token, 7);
    setTokenCookie("refresh_token", response.refresh_token, 7);
  }

  return response;
}

export async function loginPlatformUser(username, password) {
  const payload = {
    username,
    password,
  };
  const response = await apiClient.post(PLATFORM_LOGIN_PATH, payload);

  // Store tokens in secure cookies
  if (response.access_token) {
    setTokenCookie("access_token", response.access_token, 7);
    setTokenCookie("refresh_token", response.refresh_token, 7);
  }

  return response;
}

export async function getUserInfo() {
  return apiClient.get(ME_PATH);
}

export async function forgotPassword(email) {
  const payload = {
    email,
  };
  return apiClient.post(FORGOT_PASSWORD_PATH, payload);
}

export async function resendVerificationEmail(email) {
  const payload = {
    email,
  };
  return apiClient.post(RESEND_VERIFICATION_PATH, payload);
}

export async function logoutUser() {
  const refreshToken = getTokenFromCookie("refresh_token");
  const payload = {
    refresh_token: refreshToken,
  };

  try {
    await apiClient.post(LOGOUT_PATH, payload);
  } catch (error) {
    console.error("Logout API error:", error);
    // Continue with local logout even if API fails
  } finally {
    clearTokenCookies();
  }
}

export function getJWTData() {
  const accessToken = getTokenFromCookie("access_token");
  if (accessToken) {
    return decodeToken(accessToken);
  }
  return null;
}

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;

export async function refreshAccessToken() {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = getTokenFromCookie("refresh_token");

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const payload = {
        refresh_token: refreshToken,
      };

      console.log("Refreshing access token...");

      // Make direct fetch call to avoid circular dependency with apiClient
      const response = await fetch(`${API_URL}${REFRESH_PATH}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token refresh failed:", errorText);
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      console.log("Token refreshed successfully");

      // Update tokens in cookies
      if (data.access_token) {
        setTokenCookie("access_token", data.access_token, 7);
        if (data.refresh_token) {
          setTokenCookie("refresh_token", data.refresh_token, 7);
        }
      }

      return data.access_token;
    } catch (error) {
      console.error("Refresh token error:", error);
      // Clear cookies and throw error to redirect to login
      clearTokenCookies();
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
